import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import unsbscribeOsn from "@WhiteCap/Handlers/SubscriptionsDashboard/UnsubscribeOsn";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    OsnOptOutConfirmationPageUrl: getPageLinkByPageType(state, "OsnOptOutConfirmationPage")?.url,
});

const mapDispatchToProps = {
    unsbscribeOsn,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OptOutOsnStyles {
    container?: InjectableCss;
    emailTextField?: TextFieldPresentationProps;
    ctaBtn?: ButtonPresentationProps;
}

export const optOutOsnStyles: OptOutOsnStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            gap: 20px;
                        `,
                        null,
                        css`
                            flex-direction: row;
                            gap: 18px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                flex-grow: 1;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 345px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    ctaBtn: {
        variant: "primary",
        css: css`
            max-height: 40px;
            width: fit-content;
        `,
    },
};

const styles = optOutOsnStyles;

const OptOutOsn: FC<Props> = ({ unsbscribeOsn, OsnOptOutConfirmationPageUrl }) => {
    const emailInvalidMessage = siteMessage("OSN_Invalid_Email_Message");
    const emailRequiredMessage = siteMessage("OSN_Email_Required_Message");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const history = useHistory();

    const handleAddSubscriber = () => {
        if (!isSubmitted) {
            setIsSubmitted(true);
        }
        if (emailError) {
            return;
        }
        setLoading(true);
        unsbscribeOsn({
            email,
            onError: (message: string) => {
                toasterContext.addToast({
                    body: message,
                    messageType: "danger",
                });
                setLoading(false);
            },
            onSuccess: () => {
                setLoading(false);
                OsnOptOutConfirmationPageUrl && history.push(OsnOptOutConfirmationPageUrl);
            },
        });
    };

    const emailChangeHandler = (value: string) => {
        const errorMessage = !value ? emailRequiredMessage : validateEmail(value) ? "" : emailInvalidMessage;
        setEmailError(errorMessage);
        setEmail(value);
    };

    return (
        <StyledWrapper {...styles.container}>
            <TextField
                {...styles.emailTextField}
                type="email"
                onChange={e => emailChangeHandler(e.currentTarget.value)}
                value={email}
                data-test-selector="createAccount_email"
                maxLength={256}
                placeholder="Enter your email"
                error={isSubmitted && emailError}
            />
            <Button {...styles.ctaBtn} onClick={handleAddSubscriber} disabled={loading}>
                {translate("Unsusbcribe")}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OptOutOsn),
    definition: {
        group: "Common",
        displayName: "Opt Out OSN",
    },
};

export default widgetModule;
